import type { RuleListItem } from "~/lib/api/types";

/**
 * Simple NCM lookup: one NCM, an optional route and a date. Published fiscal
 * rules are grouped the way an operator reads them (in force for the route,
 * outside validity, other routes) — the same wildcard rule the engine uses
 * for an unset UF on a rule. Absence of a rule is not an exemption.
 */

export type LookupMode = "ncm" | "cest" | "product";

export type QuickRoute = { originUf?: string; destinationUf?: string; asOf: string };

export type QuickLookupGroups = {
  /** In-force rules for the route that state ICMS (what the engine uses). */
  inForce: RuleListItem[];
  /** In-force national rows that state only IPI (TIPI) and leave ICMS out. */
  nationalIpi: RuleListItem[];
  outsideValidity: RuleListItem[];
  otherRoutes: RuleListItem[];
  /** In-force ST rules for the destination's internal operation (IOB "ST interna"). */
  destinationInternalSt: RuleListItem[];
};

export function ncmDigits(value: string | undefined): string {
  return (value ?? "").replace(/\D/g, "");
}

/** 17041000 → 1704.10.00; partial codes keep the same grouping. */
export function formatNcm(value: string): string {
  const digits = ncmDigits(value);
  return [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)].filter(Boolean).join(".");
}

/**
 * Live formatting for an NCM text field: keeps at most eight digits and shows
 * them as 0000.00.00 while the operator types ("84713" → "8471.3").
 */
export function formatNcmInput(value: string): string {
  return formatNcm(ncmDigits(value).slice(0, 8));
}

export function isInForce(rule: Pick<RuleListItem, "validFrom" | "validTo">, asOf: string) {
  return (!rule.validFrom || rule.validFrom <= asOf) && (!rule.validTo || rule.validTo >= asOf);
}

export function matchesRoute(
  rule: Pick<RuleListItem, "originUf" | "destinationUf">,
  route: Pick<QuickRoute, "originUf" | "destinationUf">,
) {
  const origin = !route.originUf || rule.originUf == null || rule.originUf === route.originUf;
  const destination =
    !route.destinationUf ||
    rule.destinationUf == null ||
    rule.destinationUf === route.destinationUf;
  return origin && destination;
}

/** Mirrors the engine: a rule that marks ICMS EXCLUDED never answers for ICMS. */
export function coversIcms(rule: RuleListItem) {
  return rule.fiscalDetail?.taxes?.icms?.treatment !== "EXCLUDED";
}

export function isStRule(rule: RuleListItem) {
  const icms = rule.fiscalDetail?.taxes?.icms;
  return icms?.mode === "ST" && icms.stApplicable !== false;
}

export function groupQuickLookup(rules: RuleListItem[], route: QuickRoute): QuickLookupGroups {
  const fiscal = rules.filter((rule) => rule.kind === "FISCAL");
  const routeSet = Boolean(route.originUf || route.destinationUf);
  const inForce: RuleListItem[] = [];
  const nationalIpi: RuleListItem[] = [];
  const outsideValidity: RuleListItem[] = [];
  const otherRoutes: RuleListItem[] = [];
  for (const rule of fiscal) {
    const current = isInForce(rule, route.asOf);
    if (!coversIcms(rule)) {
      if (current && matchesRoute(rule, route)) nationalIpi.push(rule);
      continue;
    }
    if (matchesRoute(rule, route)) (current ? inForce : outsideValidity).push(rule);
    else if (current) otherRoutes.push(rule);
  }
  const destination = route.destinationUf;
  const destinationInternalSt =
    destination && route.originUf && route.originUf !== destination
      ? fiscal.filter(
          (rule) =>
            rule.originUf === destination &&
            rule.destinationUf === destination &&
            isInForce(rule, route.asOf) &&
            isStRule(rule),
        )
      : [];
  // Most specific first, like the engine's tie-breaker, then by route.
  const specificity = (rule: RuleListItem) =>
    [rule.originUf, rule.destinationUf, rule.cest].filter((value) => value != null).length;
  const byRoute = (a: RuleListItem, b: RuleListItem) =>
    specificity(b) - specificity(a) ||
    `${a.originUf ?? ""}>${a.destinationUf ?? ""}>${a.cest ?? ""}`.localeCompare(
      `${b.originUf ?? ""}>${b.destinationUf ?? ""}>${b.cest ?? ""}`,
    );
  return {
    inForce: inForce.sort(byRoute),
    nationalIpi,
    outsideValidity: outsideValidity.sort(byRoute),
    otherRoutes: routeSet ? otherRoutes.sort(byRoute) : [],
    destinationInternalSt,
  };
}

/**
 * Adjusted MVA exactly as the engine derives it (`derived-mva-v1`):
 * ((1 + MVA) × (1 − interstate) / (1 − internal)) − 1. Only meaningful for an
 * interstate ST route; returns null when inputs are missing or internal.
 */
export function derivedAdjustedMva(input: {
  originalMva?: string;
  interstateRate?: string;
  destinationInternalRate?: string;
}): number | null {
  const mva = Number(input.originalMva);
  const inter = Number(input.interstateRate);
  const internal = Number(input.destinationInternalRate);
  if (![mva, inter, internal].every(Number.isFinite) || !input.originalMva) return null;
  if (!input.interstateRate || inter <= 0 || internal >= 1) return null;
  return Math.round((((1 + mva) * (1 - inter)) / (1 - internal)) * 10_000 - 10_000) / 10_000;
}

export function todayIso(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}
